/**
 * Improvement opportunity: Roll this into AuthConfiguration.ConfigProvider.
 *
 * Doing so now would make the MR too big.
 */

import type { CookieOption } from 'next-auth';
import { TIME_UNITS } from 'src/common/constants';

export const IDP_LOGOUT_ENDPOINT = '/Logout.htm';
export const COOKIES_LOOKUP = {
    /**
     * This is set in our ./server/server.ts setup.
     */
    SESSION_GUID: 'zguid',
};

const ACCESS_TOKEN_TTL_IN_MINUTES = 15;
export const ACCESS_TOKEN_TTL = {
    IN_MINUTES: ACCESS_TOKEN_TTL_IN_MINUTES,
    IN_SECONDS: ACCESS_TOKEN_TTL_IN_MINUTES * TIME_UNITS.SECONDS_IN_MINUTE,
    IN_MILLISECONDS: ACCESS_TOKEN_TTL_IN_MINUTES * TIME_UNITS.SECONDS_IN_MINUTE * TIME_UNITS.MILLISECONDS_IN_SECOND,
};

const REFRESH_TOKEN_TTL_IN_MINUTES = TIME_UNITS.MINUTES_IN_HOUR * TIME_UNITS.HOURS_IN_DAY;
export const REFRESH_TOKEN_TTL = {
    IN_MINUTES: REFRESH_TOKEN_TTL_IN_MINUTES,
    IN_SECONDS: REFRESH_TOKEN_TTL_IN_MINUTES * TIME_UNITS.SECONDS_IN_MINUTE,
    IN_MILLISECONDS: REFRESH_TOKEN_TTL_IN_MINUTES * TIME_UNITS.SECONDS_IN_MINUTE * TIME_UNITS.MILLISECONDS_IN_SECOND,
};

export enum ZILLOW_AUTH_PROVIDER {
    AUTH0 = 'zillow-auth0',
}
export enum SessionStatus {
    AUTHENTICATED = 'authenticated',
    LOADING = 'loading',
    UNAUTHENTICATED = 'unauthenticated',
}

export const NEXT_AUTH_COOKIES = {
    SESSION_TOKEN: 'zhl-mortgage-application-center-web.auth.session-token',
    CALLBACK_URL: 'zhl-mortgage-application-center-web.auth.callback-url',
    CSRF_TOKEN: 'zhl-mortgage-application-center-web.auth.csrf-token',
    PKCE_CODE_VERIFIER: 'zhl-mortgage-application-center-web.auth.pkce.code_verifier',
    STATE: 'zhl-mortgage-application-center-web.auth.state',
    NONCE: 'zhl-mortgage-application-center-web.auth.nonce',
};

/**
 * Pulled directly from https://github.com/nextauthjs/next-auth/blob/main/packages/core/src/lib/utils/cookie.ts#L62
 * with useSecureCookies set to true.
 */
export const DEFAULT_DEPLOYMENT_COOKIE_OPTIONS: Record<
    'SESSION_TOKEN' | 'CALLBACK_URL' | 'CSRF_TOKEN' | 'PKCE_CODE_VERIFIER' | 'STATE' | 'NONCE',
    CookieOption['options']
> = {
    SESSION_TOKEN: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/homeloans',
        secure: true,
    },
    CALLBACK_URL: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/homeloans',
        secure: true,
    },
    CSRF_TOKEN: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/homeloans',
        secure: true,
    },
    PKCE_CODE_VERIFIER: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/homeloans',
        secure: true,
        maxAge: 60 * 15, // 15 minutes in seconds
    },
    STATE: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/homeloans',
        secure: true,
        maxAge: 60 * 15, // 15 minutes in seconds
    },
    NONCE: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/homeloans',
        secure: true,
    },
};

export const DEFAULT_LOCAL_COOKIE_OPTIONS: Record<
    'SESSION_TOKEN' | 'CALLBACK_URL' | 'CSRF_TOKEN' | 'PKCE_CODE_VERIFIER' | 'STATE' | 'NONCE',
    CookieOption['options']
> = {
    SESSION_TOKEN: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: true,
    },
    CALLBACK_URL: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: true,
    },
    CSRF_TOKEN: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: true,
    },
    PKCE_CODE_VERIFIER: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: true,
        maxAge: 60 * 15, // 15 minutes in seconds
    },
    STATE: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: true,
        maxAge: 60 * 15, // 15 minutes in seconds
    },
    NONCE: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: true,
    },
};

export enum SessionErrorType {
    NETWORK_ERROR = 'NetworkError',
    REFRESH_TOKEN_EXPIRED = 'RefreshTokenExpired',
    NO_ACCOUNT_FOUND = 'NoAccountFound',
    UNEXPECTED_ERROR = 'UnexpectedError',
    BAD_API_RESPONSE = 'BadAPIResponse',
    REFRESH_ERROR = 'RefreshError',
}
